/**
 * Rights & consent gates for the generation flow.
 *
 * Ported from `lib/rightsConsent/gate.ts` and — unlike the sensitive-topic rules next door —
 * reimplemented rather than served. There is no policy data to go stale here: these are two
 * boolean shapes, not a keyword list or a weighting somebody will tune. `app/domain/` holds
 * the same two rules and the routes enforce them independently, which is what actually
 * matters; a config endpoint for four lines of `&&` would be ceremony.
 *
 * This is **not** the sensitive-topic guardrail and does not replace it. Both run
 * independently, and both must pass:
 *
 * - Edit mode starts from a real uploaded photo: the uploader must confirm they have the
 *   right to use and edit it, and — if it shows identifiable people — that those people have
 *   consented to that use.
 * - Describe mode starts from nothing real: there is no photograph to have rights to, but if
 *   the brief calls for identifiable people, the requester must acknowledge that any people
 *   in the result are entirely synthetic, not a real depiction of anyone.
 */

export interface EditRightsConsentInput {
  sourceRightsConfirmed: boolean;
  depictsIdentifiablePeople: boolean;
  subjectConsentConfirmed: boolean;
}

export function isEditRightsConsentSatisfied(input: EditRightsConsentInput): boolean {
  return (
    input.sourceRightsConfirmed &&
    (!input.depictsIdentifiablePeople || input.subjectConsentConfirmed)
  );
}

export interface DescribeRightsConsentInput {
  depictsIdentifiablePeople: boolean;
  syntheticPeopleDisclosureAcknowledged: boolean;
}

export function isDescribeRightsConsentSatisfied(input: DescribeRightsConsentInput): boolean {
  return !input.depictsIdentifiablePeople || input.syntheticPeopleDisclosureAcknowledged;
}
